<template>
  <div class="card" :class="cssClass">
    <div class="card-body">
      <div class="lp-retirement-calculator">
        <div class="mb-3 mb-lg-4">
          <base-group-input inputLabel="Amount Needed at Retirement"
                            front-group="$"
                            :required-flag="true">
            <base-input label="Amount Needed at Retirement"
                        v-model.number="amountNeeded"
                        type="number"
                        placeholder="2500000"
                        :show-label="false" />
          </base-group-input>
        </div>
        <div class="mb-3 mb-lg-4">
          <base-group-input :required-flag="true" inputLabel="Average Return Rate" back-group="%">
            <base-input label="Average Return Rate"
                        v-model.number="avgReturnRate"
                        type="number"
                        step="0.1"
                        placeholder="6"
                        :show-label="false" />
          </base-group-input>
        </div>
        <div class="mb-3 mb-lg-4">
          <base-group-input inputLabel="Years Until Retirement"
                            back-group="Years"
                            :required-flag="true">
            <base-input label="Years Until Retirement"
                        v-model.number="yearsUntilRetirement"
                        type="number"
                        placeholder="20"
                        :show-label="false" />
          </base-group-input>
        </div>
        <div class="mb-3 mb-lg-4">
          <base-group-input inputLabel="Current Retirement Savings"
                            front-group="$"
                            :required-flag="true">
            <base-input label="Current Retirement Savings"
                        v-model.number="currentSavings"
                        type="number"
                        placeholder="15000"
                        :show-label="false" />
          </base-group-input>
        </div>
        <div class="calculator__total">
          <p>Monthly Savings Required</p>
          <div class="calculator-total__number">
            <span>$</span>
            <p>{{ monthlySavingsRequired.toLocaleString() }}</p>
          </div>
        </div>
        <hr />
      </div>
      <slot name="cta"></slot>
    </div>
  </div>
  <div class="lp-calculator-disclaimer">
    <slot name="disclaimer"></slot>
  </div>
</template>

<script lang="ts">
  declare const DataIntelligenceSubmitScript: any;

  import { defineComponent, ref, computed, watch } from 'vue';

  import { getMonthlyPaymentForFutureValue } from '../helpers/calculators/financial';
  import { debounce } from '../helpers/calculators/input';
  import { sendInsightReport } from '../helpers/calculators/insight';

  import BaseInput from './BaseInput.vue';
  import BaseGroupInput from './BaseGroupInput.vue';

  export default defineComponent({
    name: 'RetirementCalculator',
    components: {
      BaseInput,
      BaseGroupInput,
    },
    props: {
      cssClass: {
        type: String,
        required: false,
        default: '',
      },
    },
    setup() {
      const amountNeeded = ref(2500000);
      const avgReturnRate = ref(6);
      const currentSavings = ref(15000);
      const yearsUntilRetirement = ref(30);
      const compoundFrequency = ref(12);

      const amountToSave = computed(
        () => amountNeeded.value - currentSavings.value,
      );

      const monthlySavingsRequired = computed(() => {
        const monthlyPayment = getMonthlyPaymentForFutureValue(
          amountToSave.value,
          avgReturnRate.value,
          compoundFrequency.value,
          yearsUntilRetirement.value,
        );

        return Math.round(monthlyPayment).toLocaleString();
      });

      // Sitefinity Insight
      const reportInputToDEC = () => {
        DataIntelligenceSubmitScript._client.sentenceClient.writeSentence({
          predicate: 'Input',
          object: 'Retirement Calculator',
        });
      };
      watch(
        monthlySavingsRequired,
        debounce(() => sendInsightReport(reportInputToDEC)),
      );

      return {
        amountNeeded,
        avgReturnRate,
        currentSavings,
        monthlySavingsRequired,
        yearsUntilRetirement,
      };
    },
  });
</script>

<style scoped></style>
